import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { ChevronLeft, ChevronDown, ChevronRight, Rocket, BarChart3, Users2, Search, FileText, Eye } from "lucide-react";
import img1 from "../../../assets/images/life-journey/1.png";
import img2 from "../../../assets/images/life-journey/2.png";
import img3 from "../../../assets/images/life-journey/3.png";
import img4 from "../../../assets/images/life-journey/4.png";
import img5 from "../../../assets/images/life-journey/5.png";
import img6 from "../../../assets/images/life-journey/6.png";
import { useEffect, useState } from "react";

export const FeaturesSection = () => {
  const handleScrollToPricing = () => {
    const element = document.getElementById("pricing-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const reportImages = [img1, img2, img3, img4, img5, img6];
  const [showMore, setShowMore] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [index, setIndex] = useState(0);
  const total = reportImages.length;

  const PER_VIEW_DESKTOP = 4;
  const [dIndex, setDIndex] = useState(0);
  const desktopMaxIndex = Math.max(0, total - PER_VIEW_DESKTOP);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)"); // < sm
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // auto-swipe mobile every 1s
  useEffect(() => {
    if (!isMobile) return;
    const id = setInterval(() => {
      setIndex(i => (i + 1) % total);
    }, 1000);
    return () => clearInterval(id);
  }, [isMobile, total]);

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);
  const dPrev = () => setDIndex(i => (i <= 0 ? desktopMaxIndex : i - 1));
  const dNext = () => setDIndex(i => (i >= desktopMaxIndex ? 0 : i + 1));

  const features = [
    {
      icon: Users2,
      title: "Detailed Birth Chart (Kundli) Analysis",
      description: "ग्रहों और भावों की स्थिति का गहन अध्ययन।"
    },
    {
      icon: BarChart3,
      title: "Planetary Influence",
      description: "Career, Finance & Relationships पर प्रभाव।"
    },
    {
      icon: FileText,
      title: "Dasha & Antardasha Insights",
      description: "आने वाले वर्षों के प्रमुख बदलाव।"
    },
    {
      icon: Eye,
      title: "Personal Remedies & Solutions",
      description: "ग्रह दोष, राहु-केतु, पितृ दोष के लिए उपाय।"
    },
    {
      icon: FileText,
      title: "Gemstones & Rudraksh For you",
      description: "निजीकृत रत्न और रुद्राक्ष की पहचान और उनका उपयोग।"
    },
    {
      icon: Search,
      title: "Future Prediction",
      description: "अगले 5 साल का वर्षफल।"
    }
  ];

  const visibleFeatures = showMore ? features : features.slice(0, 3);

  return (
    <section className="bg-gradient-to-b from-background-blue to-cosmic-blue/10">

       <div className="container mx-auto px-6 py-10">
          <div className="flex flex-col justify-center items-center mb-6">
            <h2 className="text-4xl text-center text-cosmic-gold py-10">A Personal Message from Acharya Lavbhushan Ji</h2>
            <div className="relative w-full overflow-hidden rounded-lg shadow-lg pt-[56.25%]">
              <iframe
                className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/0-HMwiKfJIE?rel=0&modestbranding=1&playsinline=1"
                title="YouTube video"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          </div>
      </div> 

      <div className="container mx-auto px-6">
        {/* Rocket Icon */}
        <div className="text-center mb-12">
          <Rocket className="w-16 h-16 text-cosmic-gold mx-auto mb-6" />
        </div>

        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-sm uppercase tracking-wider text-cosmic-gold mb-4">Features</h2>
          <h3 className="text-4xl lg:text-5xl font-bold text-foreground">
            What Exactly Will You Get in Premium Life Journey Report?
          </h3>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {visibleFeatures.map((feature, index) => (
            <Card key={index} className="p-6 border-cosmic-gold/20 bg-card-blue/50 backdrop-blur-sm hover:shadow-[0_0_20px_hsl(var(--mystic-gold)/0.2)] transition-all duration-300">
              <feature.icon className="w-12 h-12 text-cosmic-gold mb-4" />
              <h4 className="text-lg font-semibold text-foreground mb-2">{feature.title}</h4>
              <p className="text-muted-foreground">{feature.description}</p>
            </Card>
          ))}
        </div>

        {/* Show More Button */}
        {!showMore && (
          <div className="text-center pb-16">
            <Button 
              variant="cosmic-outline" 
              onClick={() => setShowMore(true)}
              className="group"
            >
              Show More
              <ChevronDown className="w-4 h-4 ml-2 group-hover:translate-y-1 transition-transform" />
            </Button>
          </div>
        )}

        {/* Report Preview Carousel */}
        <div className="relative bg-cosmic-blue/20 rounded-2xl p-6 sm:p-12 backdrop-blur-sm border border-cosmic-gold/20">

          {/* --- Mobile swiper (1 per view) --- */}
          <div className="sm:hidden relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={prev}
              aria-label="Previous"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-cosmic-gold hover:bg-cosmic-gold/20"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            <div className="overflow-hidden rounded-xl">
              <div
                className="flex transition-transform duration-300"
                style={{ transform: `translateX(-${index * 100}%)` }}
              >
                {reportImages.map((src, i) => (
                  <div key={i} className="min-w-full px-1">
                    <div className="h-80 bg-cosmic-blue rounded-lg border border-cosmic-gold/30 overflow-hidden">
                      <img src={src} alt={`Report Page ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={next}
              aria-label="Next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-cosmic-gold hover:bg-cosmic-gold/20"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>

            {/* Dots */}
            <div className="mt-3 flex justify-center gap-1">
              {reportImages.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 w-1.5 rounded-full ${i === index ? "bg-cosmic-gold" : "bg-cosmic-gold/40"}`}
                />
              ))}
            </div>
          </div>

          {/* --- Desktop carousel (4 per view) --- */}
          <div className="hidden sm:block relative">
            <Button
              variant="ghost" size="icon" onClick={dPrev} aria-label="Previous"
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 text-cosmic-blue hover:bg-cosmic-blue/20"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>

            <div className="overflow-hidden rounded-xl">
              <div
                className="flex transition-transform duration-300"
                style={{ transform: `translateX(-${(dIndex * 100) / PER_VIEW_DESKTOP}%)` }}
              >
                {reportImages.map((src, i) => (
                  <div key={i} className="w-1/4 flex-shrink-0 px-2">
                    <div className="h-72 md:h-80 bg-cosmic-blue rounded-lg border border-cosmic-gold/30 overflow-hidden">
                      <img src={src} alt={`Report Page ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button
              variant="ghost" size="icon" onClick={dNext} aria-label="Next"
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-cosmic-blue hover:bg-cosmic-blue/20"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>

            {/* Dots (pages = desktopMaxIndex + 1) */}
            <div className="mt-3 flex justify-center gap-1">
              {Array.from({ length: desktopMaxIndex + 1 }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setDIndex(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 w-3 rounded-full ${i === dIndex ? "bg-cosmic-gold" : "bg-cosmic-gold/40"}`}
                />
              ))}
            </div>
          </div>

          {/* CTA / Price (unchanged) */}
          <div className="text-center mt-8">
            <Button onClick={handleScrollToPricing} variant="cosmic" size="lg" className="mb-4 sm:h-14 sm:px-12 sm:text-lg">
              GET YOUR PREMIUM <span className="hidden md:block">LIFE JOURNEY</span> REPORT NOW
            </Button>
            <div className="text-xl sm:text-2xl font-bold text-foreground">
              <span className="line-through text-muted-foreground mr-2">₹1996</span>
              <span className="text-cosmic-gold">₹996</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};